<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { UserRoleType } from '@/api/generated/api.enum';
import { TUserOrInvitation, useUsers } from '@/composables/schoolAdmin/useUsers';
import EditClassAccessDialog from '@/core/dialogs/EditClassAccessDialog.vue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import CustomButton from '@/core/elements/CustomButton.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import EditClassAccessForm from '@/core/forms/EditClassAccessForm.vue';
import UserSearchSelect from '@/core/selects/UserSearchSelect.vue';
import { IGenericUser, IUserWithPermissions } from '@/interfaces';
import { createFakeUserFromInvitation } from '@/utils/user.utils';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { now } from 'common/utils/date.utils';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  'update:users-with-access': [value: IUserWithPermissions[]];
  'update:isValid': [value: boolean];
}>();

const props = defineProps<{
  availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
  availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  label: string;
  mode: 'archive' | 'edit';
  schoolClass: Components.Schemas.SchoolClassDto | null;
  usersWithAccess: IUserWithPermissions[];
  allowInlineAdd?: boolean;
}>();
const { schoolClass, usersWithAccess, availableSchoolSubjects, availableSchoolSubjectGroups } = toRefs(props);

const { usersAndInvitations } = useUsers();

const editClassAccessFormRef = useTemplateRef('editClassAccessFormRef');
const usersWithAccessInternal = ref<IUserWithPermissions[]>([]);
const pendingInvitationEmails = ref<string[]>([]);
let nextPendingInvitationId = -1;
const pendingOptionIds = new Map<string, number>();

const handleUpdateUsersInternalOnly = (teamMembers: IUserWithPermissions[]): void => {
  usersWithAccessInternal.value = teamMembers;
};

const handleUpdateUsers = (teamMembers: IUserWithPermissions[]): void => {
  emit('update:users-with-access', teamMembers);
};

const editClassAccessDialog = useDialog({
  component: EditClassAccessDialog,
  props: {
    'onUpdate:users-with-access': handleUpdateUsersInternalOnly,
  },
});

const handleEditClassAccess = (): void => {
  assertNotNullOrUndefined(schoolClass.value);

  editClassAccessDialog.show({
    class: schoolClass.value,
    usersWithAccess: usersWithAccessInternal.value,
    availableSchoolSubjects: availableSchoolSubjects.value,
    availableSchoolSubjectGroups: availableSchoolSubjectGroups.value,
  });
};

const resolveEntryId = (entry: TUserOrInvitation): number => {
  if (entry.userRole) return entry.user.id!;
  return entry.invitation?.id ?? entry.user.id ?? 0;
};

const allUsersAndInvitations = computed<TUserOrInvitation[]>(() => {
  const base = usersAndInvitations.value;
  const existingEmails = new Set(base.map((e) => e.user.email ?? ''));

  const pendingEntries: TUserOrInvitation[] = pendingInvitationEmails.value
    .filter((email) => !!email && !existingEmails.has(email))
    .map((email) => {
      if (!pendingOptionIds.has(email)) {
        pendingOptionIds.set(email, nextPendingInvitationId--);
      }
      return {
        roleType: UserRoleType.user,
        user: { id: pendingOptionIds.get(email)!, firstName: '', lastName: '', email, isPendingInvitation: true },
        userRole: undefined,
        invitation: undefined,
      };
    });

  return [...base, ...pendingEntries];
});

const availableUserOptions = computed<IGenericUser[]>(() => {
  const selectedEmails = new Set(usersWithAccessInternal.value.map((u) => u.user.email));

  return allUsersAndInvitations.value
    .filter((entry) => {
      if (selectedEmails.has(entry.user.email ?? '')) return false;
      if (entry.invitation) {
        return now() <= new Date(entry.invitation.activeUntil);
      }
      return true;
    })
    .map((entry) => ({ ...entry.user, id: resolveEntryId(entry) }));
});

const handleUserSelected = async (user: IGenericUser | null): Promise<void> => {
  if (!user) return;

  if (user.isPendingInvitation && user.email && !pendingInvitationEmails.value.includes(user.email)) {
    pendingInvitationEmails.value.push(user.email);
  }

  const entry = allUsersAndInvitations.value.find((e) => e.user.email === user.email);
  if (!entry) return;

  const isRealUser = !!entry.userRole;
  const isPendingInvitation = user.isPendingInvitation ?? false;
  const id = isRealUser ? entry.user.id! : (entry.invitation?.id ?? nextPendingInvitationId--);
  const userDto: Components.Schemas.UserDataDto = isRealUser
    ? (entry.user as Components.Schemas.UserDataDto)
    : createFakeUserFromInvitation(id, entry.user.email ?? '');

  const newUser: IUserWithPermissions = {
    user: userDto,
    userRoleType: entry.roleType,
    permissions: {
      canManageAccessRights: true,
      canManageStudents: true,
      canManagePerformanceAssessments: true,
      canManageCompetences: true,
      subjects: undefined,
    },
    schoolSubjects: [],
    schoolClass: schoolClass.value,
    isInvitation: !isRealUser,
    isPendingInvitation,
  };

  await editClassAccessFormRef.value?.addUser(newUser);
};

const handleSubmit = (): void => {
  editClassAccessFormRef.value?.submit();
};

defineExpose({
  submit: handleSubmit,
});

watch(
  usersWithAccess,
  () => {
    usersWithAccessInternal.value = usersWithAccess.value;
  },
  { immediate: true },
);
</script>

<template>
  <div class="min-w-0">
    <div class="flex gap-sm items-center justify-between mb-md">
      <div class="font-semibold">{{ label }}</div>
      <CustomButton v-if="!allowInlineAdd" theme="link" @click="handleEditClassAccess">
        {{ $t('general.add') }}
      </CustomButton>
    </div>
    <UserSearchSelect
      v-if="allowInlineAdd"
      :user-options="availableUserOptions"
      :selected-users="usersWithAccessInternal"
      :placeholder="$t('classes.teamSelectPlaceholder')"
      :label="$t('classes.teamSelectLabel')"
      :allow-inline-add="allowInlineAdd"
      class="mb-md"
      autocomplete="off"
      @user-selected="handleUserSelected"
    />
    <ul class="flex flex-col gap-sm min-w-0">
      <EditClassAccessForm
        v-if="schoolClass"
        ref="editClassAccessFormRef"
        :available-school-subject-groups="availableSchoolSubjectGroups"
        :available-school-subjects="availableSchoolSubjects"
        :mode="mode"
        :school-class="schoolClass"
        :users-with-access="usersWithAccessInternal"
        @update:users-with-access="handleUpdateUsers"
        @update:is-valid="(val) => emit('update:isValid', val)"
      />
    </ul>
    <EmptyPlaceholder :data="usersWithAccessInternal" class="h-min" />
  </div>
</template>
